<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { Icons } from '@/models/icons.enum';
import { onClickOutside } from '@vueuse/core';
import BackstageIcon from './BackstageIcon.vue';
import BackstageInput from './BackstageInput.vue';
import { ref, computed, onMounted, watch } from 'vue';

interface Props {
  options: string[];
  optionsDataCy: string;
  name?: string;
  label?: string;
  placeholder?: string;
  modelValue?: any;
  required?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  name: '',
  label: '',
  placeholder: '',
  modelValue: '',
  required: false,
  disabled: false,
});

const { t } = useI18n();

const placeholder = props.placeholder || t('global.select_placeholder');

const emit = defineEmits<{
  change: [];
  click: [value: string];
  'update:modelValue': [value: string];
}>();

const target = ref(null);
const isInitialFocus = ref(false);
const hoveredValue = ref('');
const inputValue = ref(props.modelValue || '');
const isOptionsPopupOpen = ref(false); // this is the main flag to show/hide the options popup

// lifecycle hooks
onMounted(() => {
  if (props.modelValue) {
    inputValue.value = props.modelValue;
  }
});

// computed properties
const filteredOptions = computed(() => {
  if (isInitialFocus.value) {
    return props.options;
  }

  return props.options.filter((item) => {
    return item.toLowerCase().includes(inputValue.value.toLowerCase());
  });
});

// functions
const isSelected = (option: string) => {
  return option.toLowerCase() === inputValue.value.toLowerCase(); // to highlight the selected option
};

const isHovered = (option: string) => {
  return option.toLowerCase() === hoveredValue.value.toLowerCase(); // to highlight the hovered option
};

const resetInputValue = () => {
  const matchedOption = props.options.find((item) => {
    return item.toLowerCase() === inputValue.value.toLowerCase();
  });

  if (!matchedOption) inputValue.value = '';
};

const closeOptionsPopup = () => {
  if (isOptionsPopupOpen.value) {
    isOptionsPopupOpen.value = false;
    resetInputValue();
  }
};

const openOptionsPopup = () => {
  isInitialFocus.value = true;
  if (props.disabled) {
    return;
  }

  isOptionsPopupOpen.value = true;
};

const onItemSelect = (option: string) => {
  inputValue.value = option;
  emit('update:modelValue', inputValue.value);
  emit('click', option);
  closeOptionsPopup();
};

const navigate = (direction: 'up' | 'down') => {
  const hoverItemIndex = props.options.findIndex((item) => {
    return item.toLowerCase() === hoveredValue.value.toLowerCase();
  });

  const ifUp = direction === 'up';
  const ifDown = direction === 'down';
  const ifFirstItem = hoverItemIndex === 0;
  const ifLastItem = hoverItemIndex === props.options.length - 1;

  const firstItem = props.options[0];
  const lastItem = props.options[props.options.length - 1];
  const nextItem = props.options[hoverItemIndex + 1];
  const previousItem = props.options[hoverItemIndex - 1];

  // if no item is hovered
  if (hoverItemIndex === -1) {
    hoveredValue.value = ifDown ? firstItem : lastItem;
    return;
  }

  // if first or last item is hovered
  if ((ifUp && ifFirstItem) || (ifDown && ifLastItem)) {
    hoveredValue.value = ifDown ? firstItem : lastItem;
    return;
  }

  if (ifDown) {
    hoveredValue.value = nextItem;
  }

  if (ifUp) {
    hoveredValue.value = previousItem;
  }
};

const onKeyDownTrigger = (e: KeyboardEvent) => {
  switch (e.key) {
    case 'ArrowDown':
      navigate('down');
      break;
    case 'ArrowUp':
      navigate('up');
      break;
    case 'Enter':
      if (hoveredValue.value) {
        onItemSelect(hoveredValue.value);
      }
      break;
  }
};

watch(
  () => inputValue.value,
  (value) => {
    hoveredValue.value = value;
  },
);

onClickOutside(target, () => {
  closeOptionsPopup();
});
</script>

<template>
  <div class="combobox-cnt">
    <div class="combobox-input-cnt" ref="target" v-on:keydown="onKeyDownTrigger">
      <BackstageInput
        type="text"
        :name="name"
        :label="label"
        v-bind="$attrs"
        :required="required"
        :disabled="disabled"
        v-model="inputValue"
        class="combobox-input"
        @click="openOptionsPopup"
        :placeholder="placeholder"
        @input="() => (isInitialFocus = false)">
        <template #tooltip>
          <slot name="tooltip"></slot>
        </template>
      </BackstageInput>
      <BackstageIcon :name="Icons.CHEVRON_DOWN" class="combobox-icon" />
    </div>

    <div v-if="isOptionsPopupOpen" class="options-cnt">
      <ul class="option-item" :data-cy="optionsDataCy">
        <li
          v-for="(option, index) in filteredOptions"
          :key="index"
          :value="index"
          @mouseover="hoveredValue = option"
          :class="{
            hover: isHovered(option),
            active: isSelected(option),
          }">
          <button @click="onItemSelect(option)" class="flex-grid">
            <slot :selectOption="option">
              {{ option }}
            </slot>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.combobox-cnt {
  width: 100%;
  position: relative;
  padding-bottom: 10px;
}

.options-cnt {
  width: 100%;
  max-height: 30rem;
  z-index: 99;
  padding: 0px;
  overflow: none;
  position: absolute;
  overflow-y: scroll;
  color: var(--primary-dark-color);
  background-color: var(--shade-1);
  box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.1);
}

.option-item {
  margin: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
  list-style-type: none;
  li {
    button {
      color: var(--primary-dark-color);
      border: none;
      cursor: pointer;
      padding: 14px;
      width: 100%;
      font-size: medium;
      text-align: left;
      background-color: transparent;
    }
  }
}

.combobox-input-cnt {
  position: relative;

  .combobox-input {
    width: 100%;
  }

  .combobox-icon {
    position: absolute;
    right: 1.8rem;
    top: 42%;
    transform: scale(1.5);
  }
}

.hover {
  background-color: var(--shade-2);
}

.active {
  background-color: var(--shade-3);
}
</style>
